import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { UserPlus, Trash2, AlertCircle, Check, Shield } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminUsuarios() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newAdmin, setNewAdmin] = useState({
    email: '',
    password: '',
    nombre: ''
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/listar-admins`);
      setAdmins(response.data);
    } catch (err) {
      console.error('Error fetching admins:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Frontend validation
    if (!newAdmin.email || !newAdmin.password) {
      setError('El email y la contraseña son obligatorios');
      return;
    }

    if (!validateEmail(newAdmin.email)) {
      setError('El email ingresado no es válido. Debe tener formato: ejemplo@dominio.com');
      return;
    }

    if (newAdmin.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setCreating(true);

    try {
      await axios.post(`${API_URL}/admin/crear-admin`, newAdmin);
      setSuccess('Administrador creado correctamente');
      setNewAdmin({ email: '', password: '', nombre: '' });
      fetchAdmins();
    } catch (err) {
      // Handle Pydantic validation errors (detail is array) and regular errors (detail is string)
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic validation error - extract message
        const messages = detail.map(e => {
          if (e.loc?.includes('email')) return 'El email ingresado no es válido';
          if (e.loc?.includes('password')) return 'La contraseña no es válida';
          return e.msg || 'Error de validación';
        });
        setError(messages.join('. '));
      } else {
        setError(detail || 'Error al crear administrador');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAdmin = async (adminId, email) => {
    if (!window.confirm(`¿Estás seguro de eliminar al administrador ${email}?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/admin/eliminar-admin/${adminId}`);
      fetchAdmins();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al eliminar administrador');
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Gestión de Administradores</h1>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Crear Admin */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Crear Nuevo Administrador
            </CardTitle>
            <CardDescription>
              Los administradores tienen acceso completo al panel de gestión.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {success && (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm">
                  <Check className="w-4 h-4" />
                  {success}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre (opcional)</Label>
                <Input
                  id="nombre"
                  placeholder="Nombre del administrador"
                  value={newAdmin.nombre}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, nombre: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@ejemplo.com"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, email: e.target.value }))}
                  required
                  data-testid="admin-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, password: e.target.value }))}
                  required
                  minLength={6}
                  data-testid="admin-password-input"
                />
              </div>

              <Button type="submit" className="w-full" disabled={creating} data-testid="create-admin-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                {creating ? 'Creando...' : 'Crear Administrador'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Lista de Admins */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Administradores Actuales
            </CardTitle>
            <CardDescription>
              Lista de usuarios con rol de administrador.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : admins.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No hay administradores</p>
            ) : (
              <div className="space-y-3">
                {admins.map((admin) => (
                  <div 
                    key={admin.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {admin.nombre || admin.email}
                      </p>
                      <p className="text-sm text-gray-500">{admin.email}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteAdmin(admin.id, admin.email)}
                      data-testid={`delete-admin-${admin.email}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
